import { Injectable, inject } from '@angular/core';
import { EMPTY, exhaustMap, Observable, Subscription } from 'rxjs';

import {
  type ConfirmationDrawerConfig,
  ConfirmationDrawerService,
  type ConfirmationDrawerData,
} from '@ps/core-overlay';
import {
  SURVEY_STATUS,
  SURVEY_STATUS_LABELS,
  SurveyStatus,
} from '@ps/survey-kernel';

import { SurveyHighCpiConfirmationService } from '../survey-high-cpi-confirmation/survey-high-cpi-confirmation.service';

export interface SurveyStatusConfirmationOptions {
  readonly isProject?: boolean;
  /** Open the confirmation stacked over the current drawer. See `DrawerOpenConfig.stacked`. */
  readonly stacked?: boolean;
}

@Injectable()
export class SurveyStatusConfirmationService {
  private readonly _confirmationDrawer = inject(ConfirmationDrawerService);
  private readonly _highCpiConfirmation = inject(
    SurveyHighCpiConfirmationService,
  );

  public request(
    surveyId: number,
    fromStatus: SurveyStatus,
    toStatus: SurveyStatus,
    options: SurveyStatusConfirmationOptions = {},
  ): Observable<boolean> {
    if (fromStatus === toStatus) {
      return EMPTY;
    }

    const needsCpiCheck = toStatus === SURVEY_STATUS.LIVE;

    // When opened over another drawer, stack on top: keep a backdrop (so an
    // outside click closes this drawer first) but do not replace what is beneath.
    const drawerConfig: ConfirmationDrawerConfig = options.stacked
      ? { stacked: true }
      : {};

    if (!needsCpiCheck) {
      return this._confirmationDrawer.confirm(
        this._statusConfirmationData(fromStatus, toStatus),
        drawerConfig,
      );
    }

    return new Observable<boolean>((observer) => {
      const statusRef = this._confirmationDrawer.openRef(
        {
          ...this._statusConfirmationData(fromStatus, toStatus),
          persistOnConfirm: true,
        },
        drawerConfig,
      );

      let completed = false;
      const completeOnce = (value?: boolean): void => {
        if (completed) {
          return;
        }
        completed = true;
        if (value) {
          observer.next(value);
        }
        observer.complete();
      };

      const subs = new Subscription();

      subs.add(
        statusRef.closed$.subscribe((result) => {
          if (result !== 'confirm') {
            completeOnce();
          }
        }),
      );

      subs.add(
        statusRef.confirmed$
          .pipe(
            exhaustMap(() =>
              this._highCpiConfirmation.request(surveyId, {
                isProject: options.isProject,
                nextStatus: toStatus,
              }),
            ),
          )
          .subscribe((cpiConfirmed) => {
            if (cpiConfirmed) {
              statusRef.close('confirm');
              completeOnce(true);
            }
          }),
      );

      return () => subs.unsubscribe();
    });
  }

  private _statusConfirmationData(
    fromStatus: SurveyStatus,
    toStatus: SurveyStatus,
  ): Omit<ConfirmationDrawerData, 'persistOnConfirm'> {
    const fromLabel = SURVEY_STATUS_LABELS[fromStatus];
    const toLabel = SURVEY_STATUS_LABELS[toStatus];

    return {
      title: 'Change survey status?',
      subtitle: `${fromLabel} → ${toLabel}`,
      message: `You are about to change this survey's status from "${fromLabel}" to "${toLabel}".`,
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
    };
  }
}
